import React, { useState, useEffect, forwardRef, useImperativeHandle, useRef } from "react";
import { withStyles } from '@material-ui/styles';

import { compose } from 'redux';
import withScrollListener from "../../../../../hocs/with_scroll_listener.js";
import withShowModal from '../../../../../hocs/with_show_modal.js';

import usePrevious from "../../../../../hooks/use_previous.js";

import NavigationTabContent from "../../../components/navigation_tab_content.js";

import TableView from "../../../../../base/table/table_view.js";
import FormFields from "../../../../../base/form/form_fields.js";

import { ClearItemValue } from "../../../../../base/inputs/constants.js";

import pageStyles from "../../../../styles/page_styles.js.jsx";
import { infoPageStyles } from "../../../base_info_page.js.jsx";

import Button from "../../../../../base/buttons/button.js";

import { EditParticipantType } from "../../../../modals/constants.js";
import { CreateParticipantType } from "../../../../modals/constants.js";

import { ProjectInvestmentModel } from "../../../../../models/projects/project_investment.js";
import { ProjectParticipantModel } from "../../../../../models/projects/project_participant.js";
import ListParticipantsUseCase from "../../../../../use_cases/admin/projects/participants/list_participants_use_case.js";

import { StatisticsUpdateContext } from "./statistics_update_context.js";

const styles = theme => ({
  root: {
    position: "relative"
  },
  filters: {
    width: "100%"
  },
  button: {
    marginInlineStart: "auto",
    minWidth: "195px"
  },
  formGroup: {
    marginBottom: "15px",
    display: "inline-flex",
    width: "100%"
  }
});


const UserInvestmentsTab = forwardRef((props, ref) => {

  const pageSize = 50;

  const {
    classes, user = {},
    onScrollToEndProcessed, onDataLoaded,
    updateLastLoad, onChangeFilter } = props;
  const [investments, setInvestments] = useState([]);
  const [onceLoaded, setOnceLoaded] = useState(false);
  const [filterData, setFilterData] = useState(props.filterData);

  const previousInvestments = usePrevious(investments);

  const internalRef = useRef();

  useEffect(() => {
    internalRef.current = {
      paginationInfo:  {
        page: 1,
        perPage: pageSize,
        hasMore: true
      }
    }
  }, []);


  useEffect(() => {
    if(onceLoaded) {
      return;
    }
    if(!props.visible) {
      return;
    }
    loadInvestments();
  }, [props.visible]);

  useEffect(() => {
    const { current : { paginationInfo } } = internalRef;

    let prevCount = (previousInvestments || []).length;
    let currCount = investments.length;
    if(prevCount < currCount) {
      onDataLoaded(paginationInfo.hasMore);
    }
  }, [investments])

  useEffect(() => {
    internalRef.current = {
      paginationInfo:  {
        page: 1,
        perPage: pageSize,
        hasMore: true
      }
    }
    loadInvestments({forceReload: true});
  }, [filterData]);

  const loadInvestments = ({forceReload = false} = {}) => {
    if(!props.visible) {
      return;
    }

    const { current : { paginationInfo } } = internalRef;

    const params = {
      ...paginationInfo,
      ...filterData,
      userId: user.id
    }

    if(ListParticipantsUseCase.cancel) {
      ListParticipantsUseCase.cancel();
    }

    return ListParticipantsUseCase.execute(params)
      .then((result) => {
        const { data: { list, paginationInfo : newPaginationInfo } } = result;

        const processedList = list.map((participant) => {
          const { project, investments: newInvestments } = participant;
          return newInvestments.map((investment, index) => {
            investment.participantProject = project;
            investment.hideProjectInfo = index !== 0;
            investment.key = investment.updatedAt;
            return investment;
          })
        }).flat();

        let newList = investments.concat(processedList);
        if(forceReload) {
          newList = processedList;
        }

        setOnceLoaded(true);
        internalRef.current.paginationInfo = newPaginationInfo;
        onScrollToEndProcessed(paginationInfo.hasMore);
        setInvestments(newList);
      })
      .catch((result) => {
      });
  }

  useImperativeHandle(ref, () => ({

    onScrolledToEnd() {
      const { current : { paginationInfo } } = internalRef;
      if(!paginationInfo.hasMore) {
        return;
      }
      const { page } = paginationInfo;

      let newPaginationInfo = {
        ...paginationInfo,
        page: page + 1,
      };
      internalRef.current.paginationInfo = newPaginationInfo

      loadInvestments();
    }
  }));

  const onChangeFilterField = (newItem, field, newValue) => {
    let value = newValue;
    let fieldName = field.name;
    if(field.type === "enum" && value === ClearItemValue) {
      value = undefined;
    }
    if(field.type === "model") {
      fieldName = field.foreignKey;
      value = newItem[fieldName];

    }

    const newFilterData = {
      ...filterData,
      [fieldName]: value
    };
    setFilterData(newFilterData);
    onChangeFilter(fieldName, value);
  }

  const onInvestmentUpdated = (investment) => {
    investment.key = investment.updatedAt;
    let list = [...investments];

    const index = list.findIndex(element => element.id === investment.id);

    if(index < 0) {
      return;
    }
    list[index] = investment;
    setInvestments(list);
    updateLastLoad();
  }

  const onClickRow = investment => {
    const { showModal } = props;
    showModal(EditParticipantType, {
      investment,
      onSave: onInvestmentUpdated
    })
  }

  const onClickCreateButton = () => {
    const { showModal } = props;
    showModal(CreateParticipantType, {
      user,
      onSave: onInvestmentUpdated
    })
  }

  const columnsWhiteList = ["participantProject", "paymentMethod", "status", "activityStatus", "bonuscode", "amount", "calculatedInterest", "transferPurpose", "createdAt"];
  const columns = ProjectInvestmentModel.listFields().filter(c => columnsWhiteList.includes(c.name));

  return (
    <NavigationTabContent visible = { props.visible } >
      <div className = { classes.root } >
        <div className = { classes.formGroup }>
          <FormFields className = { classes.filters }
                      formFields = { ProjectParticipantModel.listFilters() }
                      item = { filterData }
                      onItemChange = { onChangeFilterField }
                      defaultWeight = { 3 } />
          <Button className = { classes.button }
                type = { "submit" }
                onClick = { onClickCreateButton } >
            { I18n.t("admin.projects.investment.action.new") }
          </Button>
        </div>
        <div className = { classes.list } >
          <TableView columns = { columns }
                     items = { investments }
                     onClickRow = { onClickRow } />
        </div>
      </div>
    </NavigationTabContent>
  );
});

UserInvestmentsTab.defaultProps = {
}

const Component =  compose(
  withShowModal,
  withScrollListener,
  withStyles(
    (theme) => ({
      ...pageStyles(theme),
      ...infoPageStyles(theme),
      ...styles(theme),
    }),
    { withTheme: true }
  )
)(UserInvestmentsTab);

export default (props) => {
  return (
    <StatisticsUpdateContext.Consumer>
      { context =>
        <Component { ...props }
                   updateLastLoad = { context.updateLastLoad } />
      }
    </StatisticsUpdateContext.Consumer>
  )
};
